import React, { useContext, useEffect } from "react";
import { useState } from "react";
import { Button, Card, Col, Collapse, Form, FormGroup, Row } from "react-bootstrap";
import InputMask from 'react-input-mask';
import { abpnClient } from "../../../Services/AbpnClient";
import { AuthContext } from "../../Auth/AuthProvider";
import SubspecialtyTraining, { AlternativePathwayPracticeOption } from "../../Physician/Models/SubspecialtyTraining";
import { PathwayMethod } from "../../Physician/Models/PathwayMethod";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import Examination, { KnownExaminationDefinitions, PracticePathwayOptionLookupItem } from "../Models/Examination";
import { LookupItem } from "../../Shared/Models/LookupItem";
import { ValidatorHelper } from "../../Shared/ValidatorHelper";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";

interface IProps {
    isSectionOpen: boolean;
    onSave: (subSpecialtyTraining: Array<SubspecialtyTraining>) => void;
    examination: Examination;
    applicationId: number;
    subSpecialtyTrainingList: Array<SubspecialtyTraining>;
    onOpen: () => void;
    disabled: boolean;
}

export function TrainingRequirements(props: IProps) {
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [subSpecialtyTraining, setSubspecialtyTraining] = useState<SubspecialtyTraining>(new SubspecialtyTraining());
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const alternatePathwayEndDate = getDateWithOffset(props.examination.alternativePathwayEndDate);
    const isInAlternativePathwayPeriod = alternatePathwayEndDate && alternatePathwayEndDate >= new Date();
    const hasOnePathwayOption = props.examination.pathwayOptions.length == 1;
    const authContext = useContext(AuthContext);

    var endDateFormElement = React.createRef<HTMLInputElement>();
    var practiceCertDateElement = React.createRef<HTMLInputElement>();
    const practiceBoardId: string = "practiceBoard"
    var practiceBoardElement: HTMLInputElement = null;

    useEffect(() => {
        let existing = props.subSpecialtyTrainingList.find(_ => _.applicationId === props.applicationId);
        if (existing) {
            setSubspecialtyTraining(existing);
        }
        if (hasOnePathwayOption) {
            let updated = deepCopy(subSpecialtyTraining);
            updated.pathwayMethod = props.examination.pathwayOptions[0].id;
            setSubspecialtyTraining(updated);
        }
    }, [props.subSpecialtyTrainingList])

    const saveSubspecialtyTraining = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setErrorMessage("");

        e.currentTarget.classList.add('was-validated');
        if (validateDates(subSpecialtyTraining) && e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove('was-validated');

            var training = deepCopy(subSpecialtyTraining);
            setIsLoading(true);
            const url = "/api/Physician/SaveSubspecialtyTraining?examinationId=" + props.examination.examinationId;
            abpnClient.post<Array<SubspecialtyTraining>>(url, training, authContext).then((data: Array<SubspecialtyTraining>) => {
                props.onSave(data);
                setIsLoading(false);
            });
        }
    }

    const resetTrainingValues = (subSpecialtyTraining: SubspecialtyTraining) => {
        if (subSpecialtyTraining.pathwayMethod != PathwayMethod.Practice) {
            subSpecialtyTraining.practiceTime = null;
        }
        if (subSpecialtyTraining.pathwayMethod != PathwayMethod.OtherBoard) {
            subSpecialtyTraining.practiceCertificationDate = null;
            subSpecialtyTraining.trainingInstitutionName = null;
        }
        if (subSpecialtyTraining.pathwayMethod != PathwayMethod.Fellowship) {
            subSpecialtyTraining.trainingInstitutionName = null;
            subSpecialtyTraining.startDate = null;
            subSpecialtyTraining.endDate = null;
        }
        return subSpecialtyTraining;
    }

    const validateDates = (subspecialty: SubspecialtyTraining) => {
        var isValid = true;
        if (subspecialty.pathwayMethod === PathwayMethod.Fellowship) {

            ValidatorHelper.clearCustomValidation(endDateFormElement.current);
            if (!Date.parse(subspecialty.startDate) || !Date.parse(subspecialty.endDate)) isValid = false;

            if (getDateWithOffset(subspecialty.endDate) < getDateWithOffset(subspecialty.startDate)) {
                ValidatorHelper.setCustomValidationWithFeedback(endDateFormElement.current, "Must be after start date.")
                isValid = false;
            }
        }

        if (subspecialty.pathwayMethod === PathwayMethod.OtherBoard) {
            practiceBoardElement = document.getElementById(practiceBoardId) as HTMLInputElement;

            ValidatorHelper.clearCustomValidation(practiceBoardElement);
            if (!subspecialty.practiceCertificationBoard) {
                ValidatorHelper.setCustomValidationWithFeedback(practiceBoardElement, "Must select an organization.");
                isValid = false;
            }
            
            ValidatorHelper.clearCustomValidation(practiceCertDateElement.current);
            if (!Date.parse(subspecialty.practiceCertificationDate)) {
                ValidatorHelper.setCustomValidationWithFeedback(practiceCertDateElement.current, "Must be a valid date. Expected Format: mm/dd/yyyy")
                isValid = false;
            }
        }
        if (subspecialty.pathwayMethod === PathwayMethod.Practice) {
            if (!subSpecialtyTraining.practiceTime) {
                setErrorMessage("Must select a training time")
            }
        }
        return isValid;
    }



    const updateProgramName = (value: string) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.trainingInstitutionName = value;
        setSubspecialtyTraining(updated);
    }

    const updatePracticeCertificationBoard = (value: string) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.practiceCertificationBoard = value;
        setSubspecialtyTraining(updated);
    }

    const updatePracticeLocation = (value: string) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.practiceLocation = value;
        setSubspecialtyTraining(updated);
    }

    const updateStartDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.startDate = e.target.value;

        if (Date.parse(e.target.value)) {
            updated.startDate = getDateWithOffset(e.target.value).toJSON();
        }

        setSubspecialtyTraining(updated);
    }

    const updateEndDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.endDate = e.target.value;

        if (Date.parse(e.target.value)) {
            updated.endDate = getDateWithOffset(e.target.value).toJSON();
        }
        setSubspecialtyTraining(updated);
    }

    const updatePracticeCertificationDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.practiceCertificationDate = e.target.value;

        if (Date.parse(e.target.value)) {
            updated.practiceCertificationDate = getDateWithOffset(e.target.value).toJSON();
        }
        setSubspecialtyTraining(updated);
    }

    const updateIsPartTime = (value: boolean) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.isPartTime = value;
        setSubspecialtyTraining(updated);
    }

    const updatePracticeTime = (selection: PracticePathwayOptionLookupItem) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.practiceTime = selection.name;
        switch (selection.id) {
            case AlternativePathwayPracticeOption["An average of at least 17% of their post-training clinical practice time spent in the practice of NCC (at least 7 hours per week) for the past 6 years.*"]:
                updated.percentTimeForPractice = 17;
                break;
            case AlternativePathwayPracticeOption["An average of at least 25% of their post-training clinical practice time spent in the practice of NCC (at least 10 hours per week) for the past 4 years.*"]:
                updated.percentTimeForPractice = 25;
                break;
            case AlternativePathwayPracticeOption["An average of at least 33% of their post-training clinical practice time spent in the practice of NCC (at least 13 hours per week) for the past 3 years.*"]:
                updated.percentTimeForPractice = 33;
                break;
            case AlternativePathwayPracticeOption["An average of at least 50% of their post-training clinical practice time spent in the practice of NCC (at least 20 hours per week) for the past 2 years.*"]:
                updated.percentTimeForPractice = 50;
                break;
            case AlternativePathwayPracticeOption["An average of at least 25% of their total post-training professional time spent in the practice of NCC (at least 10 hours per week) for the past 4 years.**"]:
                updated.percentTimeForPractice = 25;
                break;
            default:
                break;
        }
        setSubspecialtyTraining(updated);
    }

    const formatDefaultValue = (dateString: string) => {
        if (!dateString) return "";
        var date = getDateWithOffset(dateString);
        
        if (date == null) return "";
        var year = date.getFullYear();

        var month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;

        var day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;

        return month + '/' + day + '/' + year;
    }

    const getPathwayOptionLabel = (pathway: LookupItem) => {
        if (pathway.id === PathwayMethod.OtherBoard) return "Certified By Another Organization"
        if (pathway.id === PathwayMethod.Practice) return "Clinical Practice"
        return pathway.name;
    }

    const getDocumentationText = () => {
        if (isInAlternativePathwayPeriod) {
            return "Documentation of practice pathway is required upon request of the ABPN Credentials Department and prior to approval of application."
        }
        return "Documentation of fellowship training is required upon request of the ABPN Credentials Department and prior to approval of application."
    }
    const getGeneralRequirementsText = () => {
        if (isInAlternativePathwayPeriod && props.examination.examinationDefinitionId == KnownExaminationDefinitions.NeurocriticalCare) {
            return "Applicant must have completed a fellowship, a clinical practice, or been certified by another board during the practice pathway period of six years (2021-2026 examination administrations)"
        }
        if (isInAlternativePathwayPeriod && props.examination.examinationDefinitionId == KnownExaminationDefinitions.BrainInjuryMedicine) {
            return "Applicant must have completed a fellowship or clinical practice during the practice pathway period (2014-2022 examination administrations)"
        }
        return ""
    }
    const getFellowshipRequirementText = () => {
        var startDateYear = getDateWithOffset(props.examination.startDate).getFullYear();

        if (isInAlternativePathwayPeriod && props.examination.examinationDefinitionId == KnownExaminationDefinitions.NeurocriticalCare) {
            return `Applicant must have completed a fellowship no later than July 31, ${startDateYear} in ${props.examination.certificationName} (UCNS, CAST, or other non-ACGME accredited fellowship).`;
        }

        if (isInAlternativePathwayPeriod && props.examination.examinationDefinitionId == KnownExaminationDefinitions.BrainInjuryMedicine) {
            return `Applicant must have completed a fellowship no later than July 31, ${startDateYear} in ${props.examination.certificationName}.`;
        }
        return `Applicant must have completed an ACGME-accredited fellowship in ${props.examination.certificationName} no later than July 31, ${startDateYear}.`;
    }

    const getNotesSection = () => {
        if (isInAlternativePathwayPeriod && props.examination.examinationDefinitionId == KnownExaminationDefinitions.NeurocriticalCare) {
            return <React.Fragment>
                        <Form.Text>* This calculation is based on an average work week of 40 hours. Physicians whose total practice exceeds 40 hours per week may still use the 40 hours number as the denominator of their % calculation.</Form.Text>
                        <Form.Text>** This approach specifically applies to academic program directors, administrators, or researchers, and provides them a pathway to qualification.</Form.Text>
                    </React.Fragment>
        }
        return null
    }

    const updatePathwayMethod = (pathwayMethodId: number) => {
        var updated = deepCopy(subSpecialtyTraining);
        updated.pathwayMethod = props.examination.pathwayOptions.find(_ => _.id == pathwayMethodId).id;
        resetTrainingValues(updated);
        setSubspecialtyTraining(updated);
    }

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => {if (!props.disabled) setOpenCollapse(!openCollapse)}}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Training</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Form noValidate key={subSpecialtyTraining.pathwayMethod} onSubmit={(e) => saveSubspecialtyTraining(e)}>
                        <Row className="mt-3">
                            <Col xs={12} md={12} lg={9} xl={9}>
                                <Form.Text className="mb-3">{getGeneralRequirementsText()}</Form.Text>
                                <Form.Text className="mb-3">{getDocumentationText()}</Form.Text>
                            </Col>
                        </Row>
                        <Row  hidden={hasOnePathwayOption}>
                            <Col xs={12} md={4} lg={4} xl={4}>
                                <FormGroup controlId="PracticeOptions" className="d-flex flex-column">
                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Select Practice Pathway<span className="text-pp-primary ml-2">*</span></Form.Label>
                                    <select title="Practice Options" required value={subSpecialtyTraining.pathwayMethod} onChange={(e) => updatePathwayMethod(parseInt(e.target.value))} style={{ height: '35px' }}>
                                        <option value="" hidden>Select One...</option>
                                        {
                                            props.examination.pathwayOptions.map(pathway => {
                                                return <option key={pathway.id} value={pathway.id}>{getPathwayOptionLabel(pathway)}</option>
                                            })
                                        }
                                    </select>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            {subSpecialtyTraining.pathwayMethod === PathwayMethod.Fellowship &&
                                <React.Fragment>
                                    <Col xs={12} md={12} lg={9} xl={9}>
                                        <Form.Text className="mb-3">{getFellowshipRequirementText()}</Form.Text>
                                    </Col>
                                    <Col xs={12} md={4} lg={4} xl={4} className="pp-align-self-start">
                                        <Form.Group controlId="fellowship" className="d-flex flex-column">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Fellowship Program<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <span className="pp-font-size-smallest">Enter name and location of program</span>
                                            <Form.Control
                                                type="input"
                                                defaultValue={subSpecialtyTraining.trainingInstitutionName}
                                                onChange={(e) => updateProgramName(e.target.value)}
                                                required />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4} lg={2} xl={2} className="pp-align-self-start">
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Start<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <InputMask mask="99/99/9999"
                                                defaultValue={formatDefaultValue(subSpecialtyTraining.startDate)}
                                                onChange={(e) => updateStartDate(e)}>
                                                {(inputProps: any) => {
                                                    return <Form.Control {...inputProps} placeholder="MM/DD/YYYY" className="mt-3" required type="input" />
                                                }}
                                            </InputMask>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4} lg={2} xl={2} className="pp-align-self-start">
                                        <Form.Group>
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">End<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <InputMask
                                                mask="99/99/9999"
                                                defaultValue={formatDefaultValue(subSpecialtyTraining.endDate)}
                                                onChange={(e) => updateEndDate(e)}>
                                                {(inputProps: any) => {
                                                    return <Form.Control {...inputProps} ref={endDateFormElement} placeholder="MM/DD/YYYY" className="mt-3" required type="input" />
                                                }}
                                            </InputMask>
                                            <span className="invalid-feedback pp-font-size-smallest"></span>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={9} md={4} lg={4} xl={4}>
                                        <Form.Group controlId="isPartTime">
                                            <Row>
                                                <Col>
                                                    <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Attendance<span className="text-pp-primary ml-2">*</span></Form.Label>
                                                    <Form.Check
                                                        type="radio"
                                                        label="Part Time"
                                                        checked={subSpecialtyTraining.isPartTime}
                                                        onChange={() => updateIsPartTime(true)}
                                                        className="ml-1 mt-3" />
                                                </Col>
                                                <Col>
                                                    <Form.Label></Form.Label>
                                                    <Form.Check
                                                        type="radio"
                                                        label="Full Time"
                                                        checked={!subSpecialtyTraining.isPartTime}
                                                        onChange={() => updateIsPartTime(false)}
                                                        className="ml-1 mt-3" />
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                    </Col>
                                </React.Fragment>
                            }
                            {subSpecialtyTraining.pathwayMethod === PathwayMethod.OtherBoard &&
                                <React.Fragment>
                                    <Col xs={12} md={6} lg={6} xl={6} className="pp-align-self-start">
                                        <Form.Group controlId="otherBoardOrganizationName" className="d-flex flex-column">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Organization Name<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <select id={practiceBoardId} title="Select One" defaultValue="" onChange={(e) => updatePracticeCertificationBoard(e.target.value)} required style={{ height: '35px' }}>
                                                <option value="" hidden>Select One...</option>
                                                {
                                                    props.examination.otherBoardPathwayOptions?.map(otherBoard => {
                                                        return <option key={otherBoard.id}
                                                                    value={otherBoard.name}>{otherBoard.name}
                                                                </option>})
                                                }
                                            </select>
                                            <span className="invalid-feedback pp-font-size-smallest"></span>
                                        </Form.Group>
                                        <Form.Group controlId="practiceCertificationDate">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Certification Date<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <InputMask mask="99/99/9999"
                                                defaultValue={formatDefaultValue(subSpecialtyTraining.practiceCertificationDate)}
                                                onChange={(e) => updatePracticeCertificationDate(e)}>
                                                {(inputProps: any) => {
                                                    return <Form.Control {...inputProps} ref={practiceCertDateElement} placeholder="MM/DD/YYYY" required type="input" />
                                                }}
                                            </InputMask>
                                            <span className="invalid-feedback pp-font-size-smallest"></span>
                                        </Form.Group>
                                    </Col>
                                </React.Fragment>
                            }
                            {subSpecialtyTraining.pathwayMethod === PathwayMethod.Practice &&
                                <React.Fragment>
                                    <Col xs={12} md={4} lg={4} xl={4} className="pp-align-self-start">
                                        <Form.Group controlId="fellowship" className="d-flex flex-column">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Institution Name<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <span className="pp-font-size-smallest">Enter name of program</span>
                                            <Form.Control
                                                type="input"
                                                defaultValue={subSpecialtyTraining.trainingInstitutionName}
                                                onChange={(e) => updateProgramName(e.target.value)}
                                                required />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4} lg={4} xl={4} className="pp-align-self-start">
                                        <Form.Group controlId="fellowship" className="d-flex flex-column">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Location<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <span className="pp-font-size-smallest">Enter location of program</span>
                                            <Form.Control
                                                type="input"
                                                defaultValue={subSpecialtyTraining.practiceLocation}
                                                onChange={(e) => updatePracticeLocation(e.target.value)}
                                                required />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4} lg={2} xl={2} className="pp-align-self-start">
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Start<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <InputMask mask="99/99/9999"
                                                defaultValue={formatDefaultValue(subSpecialtyTraining.startDate)}
                                                onChange={(e) => updateStartDate(e)}>
                                                {(inputProps: any) => {
                                                    return <Form.Control {...inputProps} placeholder="MM/DD/YYYY" className="mt-3" required type="input" />
                                                }}
                                            </InputMask>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4} lg={2} xl={2} className="pp-align-self-start">
                                        <Form.Group>
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">End<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            <InputMask
                                                mask="99/99/9999"
                                                defaultValue={formatDefaultValue(subSpecialtyTraining.endDate)}
                                                onChange={(e) => updateEndDate(e)}>
                                                {(inputProps: any) => {
                                                    return <Form.Control {...inputProps} ref={endDateFormElement} placeholder="MM/DD/YYYY" className="mt-3" required type="input" />
                                                }}
                                            </InputMask>
                                            <span className="invalid-feedback pp-font-size-smallest"></span>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={9} lg={9} xl={9} className="pp-align-self-start">
                                        <Form.Group controlId="otherBoardOrganizationName" className="d-flex flex-column">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Training Time<span className="text-pp-primary ml-2">*</span></Form.Label>
                                            {props.examination.practicePathwayOptions?.map(_ => {
                                                return <Form.Check
                                                    className='mb-2'
                                                    type='radio'
                                                    required
                                                    onClick={() => updatePracticeTime(_)}
                                                    label={_.name}
                                                    id={_.name}
                                                    name="practiceTime"
                                                    defaultChecked={subSpecialtyTraining.practiceTime === _.name}
                                                    value={_.name} />
                                            })}
                                            <span className="text-danger pp-font-size-smallest">{errorMessage}</span>
                                            {getNotesSection()}
                                        </Form.Group>
                                    </Col>
                                </React.Fragment>
                            }
                        </Row>
                        <Row>
                            
                            <Col xs={2} md={1} lg={1} xl={1}>
                                <Card bsPrefix="border-0 pp-align-self-start mt-3">
                                    {isLoading ? <AbpnSpinner /> : <Button variant="pp-primary" type="submit">Continue</Button>}
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </Collapse>
            </Card.Body>
        </Card>
    );

}